import _ from 'lodash';

import {AttachmentModel} from '../../models/api/attachmentModel';
import {ChannelExternalTypesEnum} from '../../models/api/channelBehaviorsModel';
import {ChannelTypesEnum} from '../../models/api/channelCommonTypes';
import {
  channelErrorLevelPriority,
  ChannelErrorLevelsEnum,
  ChannelErrorModel,
} from '../../models/api/channelErrorModel';
import {ChannelModel} from '../../models/api/channelModel';
import {ChannelProvidersEnum} from '../../models/api/channelPreferencesModel';
import {ExternalChannelTypeModel} from '../../models/api/externalChannelTypeModel';
import {InboxModel} from '../../models/api/inboxModel';
import {MessageTypesEnum} from '../../models/api/messageModel';
import {TeammateModel} from '../../models/api/teammateModel';
import {VisualRecipientModel} from '../../models/api/visualRecipientModel';
import {ResourceMap} from '../../store/appTypes';
import {teammateCanAccessInbox} from '../modelHelpers';
import {isTeammateNamespace, isTeamNamespace} from './namespaceHelpers';

/** Channel Resource error_type  */
export enum ChannelErrorsEnum {
  MISSING_O365_SEND_PERMISSIONS = 'missing_o365_send_permissions',
}

/*
 * Helpers.
 */

export function isTeamChannel(channel: ChannelModel) {
  return isTeamNamespace(channel.namespace);
}

export function isTeammateChannel(channel: ChannelModel) {
  return isTeammateNamespace(channel.namespace);
}

export function isUserParticipatingChannel(
  user: TeammateModel,
  inboxes: ResourceMap<InboxModel>,
  channel: ChannelModel,
) {
  const inbox = inboxes[channel.inboxId];
  if (!inbox) {
    return false;
  }

  if (!teammateCanAccessInbox(user, inbox)) {
    return false;
  }

  return !isTeammateNamespace(inbox.namespace) || inbox.namespace === user.namespace;
}

export function buildPatchedChannelsForInbox(channels: ReadonlyArray<ChannelModel>, inbox: InboxModel) {
  return (
    _(channels)
      // Only update channels belonging to this inbox.
      .filter((c) => c.inboxId === inbox.id)
      // Only update channels that haven't been updated before this inbox.
      .filter((c) => c.updatedAt < inbox.updatedAt)
      // Only update channels that have the incorrect namespace.
      .filter((c) => c.namespace !== inbox.namespace)
      .map((channel) => ({
        ...channel,
        namespace: inbox.namespace,
      }))
      .value()
  );
}

export function buildChannelDisplayName(channel: ChannelModel) {
  return channel.displayName || channel.address || channel.sendAddress;
}

export function buildChannelSettingsPath(newSettings: boolean, inbox: InboxModel, channel: ChannelModel) {
  if (newSettings) {
    return `/settings/${inbox.namespace}/inboxes/edit/${inbox.id}/channels/${channel.id}`;
  }
  return `/settings/${inbox.namespace}/inboxes/edit/${inbox.id}/${channel.id}`;
}

export function getChannelMessageLengthLimit(channel: ChannelModel) {
  return channel.behaviors.messageLengthLimit;
}

export function getChannelAttachmentsMaxSize(channel: ChannelModel) {
  const {attachments} = channel.behaviors;
  return attachments && attachments.maxTotalSize;
}

export function getChannelAttachmentsMaxCount(channel: ChannelModel) {
  const {attachments} = channel.behaviors;
  return attachments && attachments.maxCount;
}

export function getChannelRecipientsCountLimit(channel: ChannelModel) {
  return channel.preferences.recipientsCountLimit;
}

export function isChannelAffectedByRecipientLimit(channel: ChannelModel) {
  return ![ChannelTypesEnum.YALO_WHATSAPP, ChannelTypesEnum.TWITTER_DM].includes(channel.type);
}

export function isChannelSupportedAttachment(channel: ChannelModel, attachment: AttachmentModel) {
  const {attachments} = channel.behaviors;
  return attachments && (!attachments.types || attachments.types.includes(attachment.contentType));
}

export function doesChannelSupportSignatures(channel: ChannelModel) {
  return channel.messageType === MessageTypesEnum.EMAIL;
}

export function channelHasError(channel: ChannelModel) {
  return Boolean(channel.error);
}

export function findHighestPriorityChannelError(channels: ReadonlyArray<ChannelModel>) {
  return channels.reduce((selected: ChannelErrorModel | undefined, {error}) => {
    if (!error) {
      return selected;
    }

    if (
      !selected ||
      channelErrorLevelPriority.indexOf(error.level) < channelErrorLevelPriority.indexOf(selected.level)
    ) {
      return error;
    }

    return selected;
  }, undefined);
}

export function isActionableError(error: ChannelErrorModel) {
  return error.level === ChannelErrorLevelsEnum.ERROR;
}

export function canSendToRecipient(channel: ChannelModel, recipient: VisualRecipientModel) {
  return channel.behaviors.supportedContactSources.includes(recipient.contact.source);
}

export function isChannelValidated(channel: ChannelModel) {
  return channel.preferences.canSend;
}

export function isReplyAllowedWithoutInboxAccess(channel: ChannelModel) {
  return channel.preferences.canReplyWithoutInboxAccess;
}

export function channelSupportsCalendarSync(channel: ChannelModel) {
  return [ChannelTypesEnum.GMAIL, ChannelTypesEnum.OFFICE365].includes(channel.type);
}

export function channelHasDnsSettings(channel: ChannelModel) {
  return (
    channel.type === ChannelTypesEnum.SMTP &&
    channel.preferences.provider &&
    [ChannelProvidersEnum.SENDGRID, ChannelProvidersEnum.MANDRILL].includes(channel.preferences.provider)
  );
}

export function isDemoChannel(channel: ChannelModel) {
  return channel.preferences.isDemo;
}

export function channelNeedsDeliverabilitySetting(channel: ChannelModel) {
  return isTeamChannel(channel) && !isDemoChannel(channel) && channelHasDnsSettings(channel);
}

export function mapChannelToChannelType(channel: ChannelModel): ChannelTypesEnum {
  if (channel.type === ChannelTypesEnum.GMAIL || channel.type === ChannelTypesEnum.OFFICE365) {
    return ChannelTypesEnum.SMTP;
  }

  return channel.type;
}

/*
 * Ported from `front` with minor changes.
 */

export function findUniqueChannelType(
  channel: ChannelModel,
  externalChannelTypes: ResourceMap<ExternalChannelTypeModel>,
) {
  if (channel.type !== ChannelTypesEnum.CUSTOM) {
    return channel.type;
  }

  if (!channel.externalChannelTypeId) {
    return ChannelTypesEnum.CUSTOM;
  }

  const externalChannelType = externalChannelTypes[channel.externalChannelTypeId];
  if (!externalChannelType || !externalChannelType.name) {
    return ChannelTypesEnum.CUSTOM;
  }

  return getExternalChannelTypeFromName(externalChannelType.name);
}

/**
 * Attempt to retrieve a more precise type than custom from external channel name.
 *
 * This method also helps to determine the channel icon used, given
 * the external channel type's name
 */
export const getExternalChannelTypeFromName = (
  externalChannelTypeName: string,
): ChannelExternalTypesEnum | ChannelTypesEnum.CUSTOM => {
  if (!externalChannelTypeName) {
    return ChannelTypesEnum.CUSTOM;
  }

  const channelNameLowerCased = externalChannelTypeName.toLowerCase();
  if (channelNameLowerCased.includes('drift')) {
    return ChannelExternalTypesEnum.DRIFT;
  }

  if (channelNameLowerCased.includes('dialpad')) {
    return ChannelExternalTypesEnum.DIALPAD;
  }

  if (channelNameLowerCased.includes('justcall')) {
    return ChannelExternalTypesEnum.JUSTCALL;
  }

  if (channelNameLowerCased.includes('yalo')) {
    return ChannelExternalTypesEnum.YALO;
  }

  if (channelNameLowerCased.includes('liveweb')) {
    return ChannelExternalTypesEnum.LIVEWEB;
  }

  if (channelNameLowerCased.includes('meya')) {
    return ChannelExternalTypesEnum.MEYA;
  }

  if (channelNameLowerCased.includes('instagram')) {
    return ChannelExternalTypesEnum.INSTAGRAM;
  }

  if (channelNameLowerCased.includes('sendbee / whatsapp')) {
    return ChannelExternalTypesEnum.SENDBEE_WHATSAPP;
  }

  if (channelNameLowerCased.includes('slack')) {
    return ChannelExternalTypesEnum.SLACK;
  }

  if (channelNameLowerCased.includes('facebook')) {
    return ChannelExternalTypesEnum.FACEBOOK;
  }

  return ChannelTypesEnum.CUSTOM;
};

export function isChannelDenied(channel: ChannelModel) {
  if (!channel.error?.type) {
    return false;
  }
  return [
    'denied',
    'no_read_page_mailboxes_perm',
    'app_perm_revoked',
    'no_manage_pages_perm',
    'facebook_perm',
  ].includes(channel.error.type);
}
